import React, { useState, Fragment, useEffect, useRef, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { showModal, hideModal } from '../../../redux/modal/modalActions';
import { showAlert } from '../../../redux/alert/alertActions';
import { selectCurrentUser } from '../../../redux/user/userSelectors';

import Icon from '../../Icon/Icon';
import { normalizeUploadFile } from '../../../utils/media';

const MAX_VIDEO_DURATION = 60;

const NewPostButton = ({
  showModal,
  hideModal,
  plusIcon,
  children,
  style,
  showAlert,
  currentUser,
}) => {
  const [file, setFile] = useState(undefined);
  const fileInputRef = useRef();
  const history = useHistory();
  const canPost = useMemo(
    () => (currentUser?.userType || '').toUpperCase() === 'ATHLETE',
    [currentUser]
  );

  const validateVideoDuration = (videoFile) =>
    new Promise((resolve, reject) => {
      const videoElement = document.createElement('video');
      const objectUrl = URL.createObjectURL(videoFile);

      videoElement.preload = 'metadata';
      videoElement.src = objectUrl;
      videoElement.onloadedmetadata = () => {
        URL.revokeObjectURL(objectUrl);
        const duration = videoElement.duration || 0;
        if (duration > MAX_VIDEO_DURATION) {
          reject(
            new Error(`Videos must be ${MAX_VIDEO_DURATION} seconds or shorter.`)
          );
          return;
        }
        resolve(duration);
      };
      videoElement.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Unable to read the selected video.'));
      };
    });

  useEffect(() => {
    if (file) {
      if (window.outerWidth > 600) {
        showModal(
          { file, hide: () => hideModal('NewPost/NewPost') },
          'NewPost/NewPost'
        );
      } else {
        history.push('/new', { file });
      }
      // Resetting the input value so you are able to
      // use the same file twice
      fileInputRef.current.value = '';
    }
  }, [file, showModal, hideModal, history]);

  const handleFileChange = async (event) => {
    const selected = event.target.files && event.target.files[0];
    if (!selected) {
      return;
    }

    try {
      const normalizedFile = await normalizeUploadFile(selected);

      const mimeType = normalizedFile.type || '';
      if (mimeType.startsWith('video/')) {
        await validateVideoDuration(normalizedFile);
      } else if (
        mimeType.startsWith('image/') ||
        /\.(heic|heif)$/i.test(normalizedFile.name || '')
      ) {
        // Accepted image types, HEIC is handled on the server if not converted client-side.
      } else {
        throw new Error('Unsupported file type. Please select an image or video.');
      }
      setFile(normalizedFile);
    } catch (error) {
      showAlert(error.message || 'Unable to use the selected file.');
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  const handleTriggerClick = () => {
    if (!canPost) {
      showAlert('Only athlete accounts can create posts.');
      return;
    }
    fileInputRef.current?.click();
  };

  return (
    <Fragment>
      <span
        role="button"
        tabIndex={0}
        onClick={handleTriggerClick}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleTriggerClick();
          }
        }}
        style={{
          cursor: canPost ? 'pointer' : 'not-allowed',
          opacity: canPost ? 1 : 0.5,
          ...style,
        }}
        className="icon"
      >
        {children ? (
          children
        ) : (
          <Icon icon={plusIcon ? 'add-circle-outline' : 'camera-outline'} />
        )}
      </span>
      <input
        id="file-upload"
        type="file"
        style={{ display: 'none' }}
        accept="image/*,video/*"
        onChange={handleFileChange}
        ref={fileInputRef}
      />
    </Fragment>
  );
};

const mapDispatchToProps = (dispatch) => ({
  showModal: (props, component) => dispatch(showModal(props, component)),
  hideModal: (component) => dispatch(hideModal(component)),
  showAlert: (text) => dispatch(showAlert(text)),
});

const mapStateToProps = createStructuredSelector({
  currentUser: selectCurrentUser,
});

export default connect(mapStateToProps, mapDispatchToProps)(NewPostButton);
